import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchAdminTenancyDocuments,
  uploadAdminTenancyDocument,
  fetchAdminTenancyDocumentDownloadUrl,
  deleteAdminTenancyDocument,
} from "../../../api/adminData";
import { formatDateOnly } from "../../../utils/datetime";
import { tableClass, tdCellClass, thCellClass } from "./tenantDetailStyles";

// The category set, mirroring ALLOWED_TENANCY_DOCUMENT_CATEGORIES on the
// backend. Order = dropdown order; the map drives the German labels shown in
// the table + select.
//
// A category says what a document IS. Whether it arrived by hand or was
// archived automatically is a different question, answered by
// `source_entity_type` — which is why "Schlüsselquittung" is offered for manual
// upload too.
const TENANCY_DOCUMENT_CATEGORIES = [
  { value: "rent_contract", label: "Mietvertrag" },
  { value: "residence_registration", label: "Wohnsitzanmeldung" },
  { value: "deposit_contract", label: "Mietkautionspolice" },
  { value: "deposit_payment_proof", label: "Kautions-Einzahlungsbeleg" },
  { value: "deposit_return_proof", label: "Kautions-Rückzahlungsbeleg" },
  { value: "handover_protocol", label: "Übergabeprotokoll" },
  { value: "key_receipt", label: "Schlüsselquittung" },
  { value: "damage_evidence", label: "Schadensbeleg" },
  { value: "repair_invoice", label: "Reparaturrechnung" },
  { value: "other", label: "Sonstiges" },
];

// Same pill geometry as the lifecycle badges in `tenantDetailStyles`, in the
// muted tone: this marks a fact about the row's origin, not a status that
// needs attention.
const SYSTEM_DOC_BADGE_CLASS =
  "inline-flex items-center rounded-full border border-black/10 bg-slate-100 px-2.5 py-0.5 text-[10px] font-semibold text-[#64748b] dark:border-white/[0.08] dark:bg-white/[0.04] dark:text-[#8090b0]";

/**
 * A row that mirrors a signed key receipt or handover protocol.
 *
 * Checks either half of the source pair, matching the backend's delete guard:
 * the DB CHECK makes a half-set pair impossible, but a row that even looks like
 * a projection must not offer a delete, because its `object_key` is the
 * source's only signed PDF.
 */
function isSystemDocument(doc) {
  return (
    (doc?.source_entity_type ?? null) !== null ||
    (doc?.source_entity_id ?? null) !== null
  );
}

const TENANCY_DOCUMENT_CATEGORY_LABELS = TENANCY_DOCUMENT_CATEGORIES.reduce(
  (acc, c) => {
    acc[c.value] = c.label;
    return acc;
  },
  {}
);

function categoryLabel(raw) {
  if (!raw) return "—";
  return TENANCY_DOCUMENT_CATEGORY_LABELS[String(raw)] || String(raw);
}

// Exported so the tenancy Dokumente tab labels an upload's category from the
// SAME category map the upload form offers, not a copy of it.
export { categoryLabel as tenancyDocumentCategoryLabel };

export default function TenancyDocumentsSection({
  tenancyId,
  onCountChange,
  hideHeading,
  // M1c-E: a monotonic counter the card raises after a signature finished. The
  // load effect below depends on it, so a signed key receipt or handover
  // protocol appears here without a reload. Nothing about WHAT is shown
  // changes: the projection is an ordinary tenancy document, and the backend
  // wrote it in the signing transaction. This only decides WHEN we ask again.
  refreshKey = 0,
}) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);

  const reload = useCallback(async () => {
    if (!tenancyId) {
      setDocs([]);
      return;
    }
    setLoading(true);
    try {
      const items = await fetchAdminTenancyDocuments(tenancyId);
      const list = Array.isArray(items) ? items : [];
      setDocs(list);
      onCountChange?.(list.length);
    } catch (err) {
      setUploadError(err?.message || "Dokumente konnten nicht geladen werden.");
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [tenancyId, onCountChange]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!tenancyId) {
        setDocs([]);
        return;
      }
      setLoading(true);
      try {
        const items = await fetchAdminTenancyDocuments(tenancyId);
        const list = Array.isArray(items) ? items : [];
        if (!cancelled) {
          setDocs(list);
          onCountChange?.(list.length);
        }
      } catch (err) {
        if (!cancelled) {
          setUploadError(err?.message || "Dokumente konnten nicht geladen werden.");
          setDocs([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // refreshKey joins the SAME effect rather than getting one of its own: two
    // effects would both fire on mount and cost a duplicate request.
  }, [tenancyId, onCountChange, refreshKey]);

  function handlePick() {
    if (!category.trim()) {
      setUploadError("Bitte zuerst eine Kategorie wählen.");
      return;
    }
    fileInputRef.current?.click();
  }

  async function handleSelected(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !tenancyId) return;
    if (!category.trim()) {
      setUploadError("Bitte zuerst eine Kategorie wählen.");
      return;
    }
    setUploading(true);
    setUploadError("");
    try {
      await uploadAdminTenancyDocument(tenancyId, f, { category: category.trim() });
      setCategory("");
      await reload();
    } catch (err) {
      setUploadError(err?.message || "Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  }

  async function handleOpen(docId) {
    try {
      const data = await fetchAdminTenancyDocumentDownloadUrl(docId);
      if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      window.alert(err?.message || "Download konnte nicht gestartet werden.");
    }
  }

  async function handleDelete(docId) {
    if (!window.confirm("Dokument wirklich löschen?")) return;
    try {
      await deleteAdminTenancyDocument(docId);
      await reload();
    } catch (err) {
      window.alert(err?.message || "Löschen fehlgeschlagen.");
    }
  }

  const uploadDisabled = uploading || !tenancyId || !category.trim();

  return (
    <div className="mt-6 border-t border-black/10 pt-6 dark:border-white/[0.05]">
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "12px",
          marginBottom: "10px",
        }}
      >
        {!hideHeading ? (
          <p className="m-0 text-[10px] font-bold uppercase tracking-[0.8px] text-[#64748b] dark:text-[#6b7a9a]">
            Dokumente
          </p>
        ) : null}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "10px",
            justifyContent: "flex-end",
          }}
        >
          <label className="flex items-center gap-2 text-[13px] text-[#64748b] dark:text-[#6b7a9a]">
            <span>Kategorie</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={uploading || !tenancyId}
              className={`rounded-[9px] border border-black/10 bg-slate-100 px-2 py-1.5 text-[13px] text-[#0f172a] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#eef2ff] ${
                uploading || !tenancyId
                  ? "cursor-not-allowed opacity-60 dark:bg-white/[0.04]"
                  : "cursor-pointer"
              }`}
            >
              <option value="">—</option>
              {TENANCY_DOCUMENT_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: "none" }}
            onChange={handleSelected}
          />
          <button
            type="button"
            onClick={handlePick}
            disabled={uploadDisabled}
            className="rounded-[8px] border border-black/10 bg-transparent px-3 py-2 text-[13px] font-semibold text-[#64748b] hover:bg-slate-100 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.04] disabled:cursor-not-allowed disabled:opacity-50"
            style={{ cursor: uploadDisabled ? "not-allowed" : "pointer" }}
          >
            {uploading ? "Wird hochgeladen …" : "Hochladen"}
          </button>
        </div>
      </div>
      {uploadError ? (
        <p style={{ margin: "0 0 8px 0", fontSize: "13px", color: "#f87171" }}>
          {uploadError}
        </p>
      ) : null}
      {loading ? (
        <p className="m-0 text-sm text-[#64748b] dark:text-[#6b7a9a]">Lade Dokumente …</p>
      ) : docs.length === 0 ? (
        <p className="m-0 text-sm text-[#64748b] dark:text-[#6b7a9a]">Keine Dokumente vorhanden</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={thCellClass}>Datei</th>
                <th className={thCellClass}>Kategorie</th>
                <th className={thCellClass}>Datum</th>
                <th className={thCellClass}>Hochgeladen von</th>
                <th className={thCellClass}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((doc) => {
                const isSystem = isSystemDocument(doc);
                return (
                <tr key={String(doc.id)}>
                  <td className={`${tdCellClass} font-semibold text-[#0f172a] dark:text-[#eef2ff]`}>
                    {doc.file_name || "—"}
                  </td>
                  <td className={`${tdCellClass} text-[#64748b] dark:text-[#6b7a9a]`}>
                    {categoryLabel(doc.category)}
                  </td>
                  <td className={`${tdCellClass} text-[#64748b] dark:text-[#6b7a9a]`}>
                    {formatDateOnly(doc.created_at) || "—"}
                  </td>
                  {/* "Hochgeladen von" is exactly the question the badge
                      answers: nobody uploaded this, it was archived from the
                      signed original. Better here than as a floating pill —
                      it replaces an em dash that said nothing. */}
                  <td className={`${tdCellClass} text-[#64748b] dark:text-[#6b7a9a]`}>
                    {isSystem ? (
                      <span className={SYSTEM_DOC_BADGE_CLASS}>
                        Automatisch archiviert
                      </span>
                    ) : doc.uploaded_by_name != null && doc.uploaded_by_name !== "" ? (
                      doc.uploaded_by_name
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className={tdCellClass}>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px" }}>
                      <button
                        type="button"
                        onClick={() => handleOpen(doc.id)}
                        className="border-none bg-transparent p-0 text-[13px] font-semibold text-[#7aaeff] underline hover:text-[#a8c4ff]"
                        style={{ cursor: "pointer" }}
                      >
                        Öffnen
                      </button>
                      {/* No delete for a projection. The backend answers 409,
                          and a control whose only outcome is an error dialog is
                          worse than no control. Opening still works — the file
                          is the point of the row. */}
                      {isSystem ? null : (
                        <button
                          type="button"
                          onClick={() => handleDelete(doc.id)}
                          className="border-none bg-transparent p-0 text-[13px] font-semibold text-[#64748b] dark:text-[#6b7a9a] underline hover:text-[#f87171]"
                          style={{ cursor: "pointer" }}
                        >
                          Löschen
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
